import { Fragment, memo } from 'react'

import { IconChevronDown } from '@tabler/icons-react'
import { kebabCase } from 'lodash'
import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'
import ReactMarkdown from 'react-markdown'

import { usePage } from '@subaio/components/src/Router/usePage'
import { isNonNullable } from '@subaio/utils'

import { ButtonSolid } from 'components/Buttons'
import { components } from 'components/DefaultMdxComponents'
import { Heading } from 'components/Heading'

import { Endpoint } from '../Endpoint'
import { Content } from './Content'
import { Header } from './Header'
import { Item } from './Item'
import { Label } from './Label'
import { List } from './List'
import { Method } from './Method'
import { Path } from './Path'

type Props = {
	title: string
	description?: string
	paths: Array<{
		path: string
		methods: Array<{ id: string; method: string }>
	}>
	apiDocument: OpenApi3.Document
	isOpen: boolean

	onSetIsOpen: (isOpen: boolean) => void
}

export const EndpointGroup = memo(({ title, description, paths, apiDocument, isOpen = false, onSetIsOpen }: Props) => {
	const page = usePage()!

	return (
		<>
			<Header id={kebabCase(title)}>
				<Content>
					<div>
						<Heading level={1}>{title}</Heading>
						{description && <ReactMarkdown components={components}>{description}</ReactMarkdown>}
					</div>
					<div>
						<p>Endpoints</p>
						<List>
							{paths.map(({ path, methods }, index) => (
								<Fragment key={index}>
									{methods.map(({ method, id }, index) => (
										<li key={index}>
											<Item {...page} fragment={id}>
												<Method method={method} />
												<Path>{path}</Path>
											</Item>
										</li>
									))}
								</Fragment>
							))}
						</List>
					</div>
				</Content>
				{!isOpen && (
					<ButtonSolid
						label={
							<Label>
								SHOW
								<IconChevronDown />
							</Label>
						}
						onClick={() => onSetIsOpen(true)}
					/>
				)}
			</Header>
			<div>
				{isOpen &&
					paths.map(({ path }) => {
						if (!apiDocument.paths?.[path]) throw new Error(`Could not find the path "${path}" in the spec.`)
						const methods = Object.entries(apiDocument.paths[path]!)
							.map(([key, value]) => (typeof value !== 'string' && 'tags' in value ? { method: key, ...value } : null))
							.filter(isNonNullable)

						return (
							<>
								{methods.map((method) => (
									<Endpoint
										key={`${path} ${method.method}`}
										title={title}
										path={path}
										operationId={method.operationId!}
										definitions={apiDocument.components?.schemas}
										documentParameters={apiDocument.components?.parameters}
										securityDefinitions={apiDocument.components?.securitySchemes}
										{...method}
										requestBody={method.requestBody ?? undefined}
										parameters={method.parameters ?? undefined}
										security={method.security ?? undefined}
										style={{ flex: 1, width: '100%' }}
									/>
								))}
							</>
						)
					})}
			</div>
		</>
	)
})
